import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import { astraAPI } from '../../../services/astraApi';
import { SkeletonList } from '../../../components/ui/Skeleton';

/**
 * Watchlist Status Component (2025-12-30)
 *
 * Provides real-time visibility into ASTRA's dual watchlist systems:
 * 1. Dynamic Watchlist (Ultra-Fast): Volatility-based, 60-second rotation
 * 2. Catalyst Watchlist (AI Pipeline): News/event-based, catalyst windows
 *
 * This component addresses the "visibility gap" - users can now understand
 * WHY certain symbols are being traded and HOW the watchlist systems work.
 */

const WatchlistStatus = ({ refreshTrigger = 0 }) => {
  const [watchlistData, setWatchlistData] = useState(null);
  const [loading, setLoading] = useState(true);
  // 🔧 2025-12-30 FIX: Prevent flickering on refresh - only show skeleton on INITIAL load
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('ultra_fast');
  const [countdown, setCountdown] = useState(null);

  useEffect(() => {
    fetchWatchlistStatus();
  }, [refreshTrigger]);

  // Countdown timer for next rotation
  useEffect(() => {
    if (watchlistData?.dynamic_watchlist?.next_rotation_in_seconds) {
      setCountdown(watchlistData.dynamic_watchlist.next_rotation_in_seconds);

      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            // Refresh data when rotation happens
            fetchWatchlistStatus();
            return watchlistData.dynamic_watchlist.rotation_interval_seconds;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [watchlistData?.dynamic_watchlist?.next_rotation_in_seconds]);

  const fetchWatchlistStatus = async () => {
    try {
      // 🔧 2025-12-30 FIX: Only show loading skeleton on INITIAL load
      if (isInitialLoad) {
        setLoading(true);
      }
      setError(null);

      const data = await astraAPI.getWatchlistStatus();
      setWatchlistData(data);
    } catch (err) {
      console.error('Failed to fetch watchlist status:', err);
      setError('Unable to load watchlist status');
    } finally {
      setLoading(false);
      // 🔧 FIX: Mark initial load complete - subsequent refreshes won't show skeleton
      setIsInitialLoad(false);
    }
  };

  const formatCountdown = (seconds) => {
    if (!seconds && seconds !== 0) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // 🔧 2025-12-30 FIX: Only show skeleton on INITIAL load, not on refreshes
  if (loading && isInitialLoad) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="bg-card border border-border rounded-lg p-4"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-foreground">Watchlist Systems</h2>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-warning rounded-full animate-live-pulse"></div>
            <span className="text-xs text-muted-foreground">Loading...</span>
          </div>
        </div>
        <SkeletonList items={3} />
      </motion.div>
    );
  }

  if (error || !watchlistData) {
    return (
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-foreground">Watchlist Systems</h2>
          <div className="w-2 h-2 bg-error rounded-full"></div>
        </div>
        <div className="text-center py-4">
          <Icon name="AlertTriangle" size={20} className="text-error mx-auto mb-2" />
          <p className="text-xs text-muted-foreground">{error || 'No data'}</p>
          <button
            onClick={fetchWatchlistStatus}
            className="mt-2 px-2 py-1 text-xs bg-primary text-primary-foreground rounded hover:bg-primary/90"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const { dynamic_watchlist, catalyst_watchlist, symbol_assignments } = watchlistData;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="bg-card border border-border rounded-lg p-4 glass-card-hover"
    >
      {/* Header with tabs */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
            <Icon name="ListFilter" size={16} className="text-white" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-foreground">Watchlist Systems</h2>
            <p className="text-xs text-muted-foreground">Asset selection engines</p>
          </div>
        </div>

        {/* Tab selector */}
        <div className="flex space-x-1 bg-muted rounded-lg p-0.5">
          <button
            onClick={() => setActiveTab('ultra_fast')}
            className={`px-2 py-1 text-xs font-medium rounded-md transition-colors ${
              activeTab === 'ultra_fast'
                ? 'bg-card text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Ultra-Fast
          </button>
          <button
            onClick={() => setActiveTab('ai_pipeline')}
            className={`px-2 py-1 text-xs font-medium rounded-md transition-colors ${
              activeTab === 'ai_pipeline'
                ? 'bg-card text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            AI Pipeline
          </button>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {activeTab === 'ultra_fast' ? (
          <motion.div
            key="ultra_fast"
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 10 }}
            transition={{ duration: 0.2 }}
          >
            {/* Ultra-Fast Watchlist Section */}
            <div className="space-y-3">
              {/* Status bar with rotation countdown */}
              <div className="flex items-center justify-between bg-blue-500/10 rounded-lg px-3 py-2">
                <div className="flex items-center space-x-2">
                  <Icon name="Zap" size={14} className="text-blue-500" />
                  <span className="text-xs font-medium text-foreground">
                    Volatility-Based Selection
                  </span>
                </div>
                <div className="flex items-center space-x-3">
                  <span className="text-xs text-muted-foreground">
                    Pool: {dynamic_watchlist.pool_size} symbols
                  </span>
                  <div className="flex items-center space-x-1 bg-muted px-2 py-0.5 rounded">
                    <Icon name="Timer" size={12} className="text-blue-500" />
                    <span className="text-xs font-mono font-bold text-foreground">
                      {formatCountdown(countdown)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Active symbols grid */}
              <div className="grid grid-cols-3 gap-2">
                {dynamic_watchlist.active_symbols?.slice(0, 6).map((symbol, idx) => {
                  const opp = dynamic_watchlist.top_opportunities?.find(o => o.symbol === symbol);
                  return (
                    <div
                      key={symbol}
                      className="bg-muted/50 rounded-lg px-2 py-1.5 flex items-center justify-between"
                    >
                      <span className="text-xs font-medium text-foreground truncate">
                        {symbol.replace('USDT', '')}
                      </span>
                      {opp && (
                        <span className={`text-xs font-bold ${
                          opp.score >= 80 ? 'text-success' :
                          opp.score >= 60 ? 'text-warning' : 'text-muted-foreground'
                        }`}>
                          {opp.score?.toFixed(0) || '-'}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Selection criteria */}
              <div className="flex items-center justify-between text-xs text-muted-foreground pt-2 border-t border-border">
                <span>Min volatility: {dynamic_watchlist.selection_criteria?.min_volatility || 2}%</span>
                <span className={`flex items-center space-x-1 ${
                  dynamic_watchlist.status === 'active' ? 'text-success' : 'text-warning'
                }`}>
                  <div className={`w-1.5 h-1.5 rounded-full ${
                    dynamic_watchlist.status === 'active' ? 'bg-success' : 'bg-warning'
                  }`}></div>
                  <span>{dynamic_watchlist.status || 'unknown'}</span>
                </span>
              </div>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="ai_pipeline"
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -10 }}
            transition={{ duration: 0.2 }}
          >
            {/* AI Pipeline Catalyst Watchlist Section */}
            <div className="space-y-3">
              {/* Status bar */}
              <div className="flex items-center justify-between bg-purple-500/10 rounded-lg px-3 py-2">
                <div className="flex items-center space-x-2">
                  <Icon name="Newspaper" size={14} className="text-purple-500" />
                  <span className="text-xs font-medium text-foreground">
                    Catalyst-Based Selection
                  </span>
                </div>
                <div className="flex items-center space-x-3">
                  <span className="text-xs text-muted-foreground">
                    {catalyst_watchlist.watchlist_size} catalysts
                  </span>
                  <span className="text-xs text-success">
                    {catalyst_watchlist.active_window?.length || 0} active
                  </span>
                </div>
              </div>

              {/* Active catalysts list */}
              <div className="space-y-2">
                {catalyst_watchlist.active_catalysts?.slice(0, 4).map((catalyst, idx) => (
                  <div
                    key={catalyst.symbol}
                    className="flex items-center justify-between bg-muted/50 rounded-lg px-2 py-1.5"
                  >
                    <div className="flex items-center space-x-2">
                      <div className="w-6 h-6 rounded bg-purple-500/20 flex items-center justify-center">
                        <span className="text-xs font-bold text-purple-500">
                          {catalyst.symbol?.slice(0, 2)}
                        </span>
                      </div>
                      <div>
                        <span className="text-xs font-medium text-foreground">
                          {catalyst.symbol}
                        </span>
                        <span className="text-xs text-muted-foreground ml-2">
                          {catalyst.catalyst_type || 'news'}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`text-xs font-bold ${
                        catalyst.score >= 70 ? 'text-success' :
                        catalyst.score >= 50 ? 'text-warning' : 'text-muted-foreground'
                      }`}>
                        {catalyst.score?.toFixed(0) || '-'}
                      </span>
                      <span className="px-1.5 py-0.5 bg-success/20 text-success text-xs rounded">
                        Active
                      </span>
                    </div>
                  </div>
                ))}

                {(!catalyst_watchlist.active_catalysts || catalyst_watchlist.active_catalysts.length === 0) && (
                  <div className="text-center py-3 text-xs text-muted-foreground">
                    No active catalysts detected
                  </div>
                )}
              </div>

              {/* Pending catalysts preview */}
              {catalyst_watchlist.pending_catalysts?.length > 0 && (
                <div className="pt-2 border-t border-border">
                  <span className="text-xs text-muted-foreground">
                    {catalyst_watchlist.pending_catalysts.length} pending catalysts
                  </span>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Symbol exclusivity status */}
      {symbol_assignments.overlap_detected && (
        <div className="mt-3 bg-error/10 border border-error/30 rounded-lg px-3 py-2">
          <div className="flex items-center space-x-2">
            <Icon name="AlertTriangle" size={14} className="text-error" />
            <span className="text-xs font-medium text-error">
              Symbol overlap detected: {symbol_assignments.overlap_symbols?.join(', ')}
            </span>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between mt-3 pt-2 border-t border-border text-xs text-muted-foreground">
        <span>
          {symbol_assignments.ultra_fast?.length || 0} ultra-fast, {symbol_assignments.ai_pipeline?.length || 0} AI pipeline
        </span>
        <button
          onClick={fetchWatchlistStatus}
          className="flex items-center space-x-1 hover:text-foreground transition-colors"
        >
          <Icon name="RefreshCw" size={12} />
          <span>Refresh</span>
        </button>
      </div>
    </motion.div>
  );
};

export default WatchlistStatus;
